/**
 * The scene editor's keyboard shortcuts (spec 019), extracted from
 * SceneEditor so they can be rendered and tested alone.
 *
 * Ignored while focus is in a text field, select or contenteditable, or
 * while a modal is open. Space is also ignored on a focused control (button,
 * link, checkbox, radio, or role button/tab/menuitem/switch), which keeps its
 * native Space behaviour.
 */

import { useEffect } from 'react';
import { useSceneStore } from '../stores/sceneStore';
import { timelineLengthMs, previewDurationMs } from '../utils/sceneDuration';

/** One frame at 30 fps. */
export const FRAME_MS = 1000 / 30;

/** Inputs where typing edits text (the browser's own undo applies there). */
const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'image']);

export function isTextField(target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement {
  if (target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(target.type);
}

const SPACE_CONTROL_ROLES = new Set(['button', 'tab', 'menuitem', 'switch']);

/** A focused control that keeps its native Space behaviour. */
export function isSpaceControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement) return true;
  if (target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio' || NON_TEXT_INPUT_TYPES.has(target.type))) return true;
  const role = target.getAttribute('role');
  return role !== null && SPACE_CONTROL_ROLES.has(role);
}

export function isEditingTarget(target: EventTarget | null): boolean {
  return (
    isTextField(target) ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export function isModalOpen(): boolean {
  return typeof document !== 'undefined' &&
    document.querySelector('.modal.show, [aria-modal="true"]') !== null;
}

/** `enabled` false (the scene studio, spec 029) installs nothing. */
export function useSceneShortcuts(enabled = true): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target) || isModalOpen()) return;

      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdKey = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();
      const st = useSceneStore.getState();
      const keyframeMode = st.focusedPane === 'timeline' && st.selectedKeyframes.length > 0;
      // While ▶ Preview plays nothing edits: only Escape, Space (stop) and Shift+Space (from start) act (spec 009)
      if (st.isPlaying && e.key !== 'Escape' && e.key !== ' ') return;

      // Undo / redo / save
      if (cmdKey && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        st.undo();
        return;
      }
      if (cmdKey && ((key === 'z' && e.shiftKey) || key === 'y')) {
        e.preventDefault();
        st.redo();
        return;
      }
      if (cmdKey && key === 's') {
        e.preventDefault();
        st.saveScene();
        return;
      }

      // Clipboard: keyframes when the timeline is focused and keyframes are
      // selected (paste: when the keyframe clipboard holds some), else elements.
      if (cmdKey && key === 'c') {
        e.preventDefault();
        if (keyframeMode) st.copyKeyframes();
        else st.copy();
        return;
      }
      if (cmdKey && key === 'v') {
        e.preventDefault();
        if (st.focusedPane === 'timeline' && st.keyframeClipboard?.length) st.pasteKeyframes();
        else st.paste();
        return;
      }
      if (cmdKey && key === 'x') {
        e.preventDefault();
        if (keyframeMode) {
          st.copyKeyframes();
          st.deleteKeyframes(st.selectedKeyframes);
        } else {
          st.cut();
        }
        return;
      }

      // Delete: focus-aware (keyframes in the timeline, else elements)
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        st.handleDelete();
        return;
      }

      // Escape: stop the preview; else clear the keyframe selection, then the element selection
      if (e.key === 'Escape') {
        e.preventDefault();
        if (st.isPlaying) st.stop();
        else if (st.selectedKeyframes.length > 0) st.clearKeyframeSelection();
        else st.clearSelection();
        return;
      }

      // Space: start (from the playhead) / stop the preview; Shift+Space:
      // "⏮ From start" (spec 009 D-2), also restarting a running preview.
      // Neither acts while a control has focus.
      if (e.key === ' ') {
        if (isSpaceControl(e.target)) return;
        e.preventDefault();
        if (e.shiftKey) st.playFromStart();
        else if (st.isPlaying) st.stop();
        else st.play();
        return;
      }

      // Frame step: , and . (one frame at 30 fps), clamped to [0, length]
      if (e.key === ',' || e.key === '.') {
        e.preventDefault();
        const length = timelineLengthMs(st.scene, 0, previewDurationMs(st.scene, st.keyframes));
        const next = st.playheadPosition + (e.key === ',' ? -FRAME_MS : FRAME_MS);
        st.setPlayhead(Math.min(length, Math.max(0, next)));
        return;
      }

      // Arrow keys: nudge the single selected sprite 1 canvas px (10 with Shift)
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !cmdKey) {
        if (st.selection.type !== 'sprite' || st.selection.ids.length !== 1) return;
        const spriteId = st.selection.ids[0];
        if (!st.scene?.sprites.some((s) => s.id === spriteId)) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        st.nudgeSprite(spriteId, dx, dy);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}

export default useSceneShortcuts;
